import React from "react";
import { StylesConfig } from "react-select";
export interface Column<T> {
  header: string;
  key: keyof T;
  enableLink: boolean;
  viewButton?: boolean;
  path?: string;
}

// Custom styles for react-select
export const CustomSelectStyles: StylesConfig = {
  control: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    padding: "2px", // Adjust padding as needed
  }),
};
interface CustomTableProps<T> {
  columns: Column<T>[];
  data: T[];
  children?: React.ReactNode;
  idKeyName: string;
  keyID2?: string;
  currentPageNumber?: number;
  handlePageClick?: () => void;
  modelForm?: boolean;
  viewButton?: boolean;
  showFilter?: boolean;
  handleView?: (data: T) => void;
  handleModel?: (data: number) => void | undefined;
  handleNavigation: (path: string | undefined, states: number, memberID?: number | null) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTable: React.FC<CustomTableProps<any> & { 
  showCheckbox?: boolean;
  selectedIds?: any[];
  onSelectionChange?: (ids: any[]) => void;
}> = ({
  columns,
  data,
  children,
  idKeyName,
  keyID2,
  modelForm = false,
  viewButton = false,
  handleView,
  handleModel,
  handleNavigation,
  showCheckbox = false,
  selectedIds = [],
  onSelectionChange,
}) => {
  const handleEdit = (selectedOption: number) => {
    handleModel?.(selectedOption);
  };

  const isAllSelected = data.length > 0 && data.every((item) => selectedIds.includes(item[idKeyName]));

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = data.map((item) => item[idKeyName]);
      // Merge with existing selectedIds to avoid deselecting items from other pages if we supported that (but typically we select current page)
      // For simplicity and common behavior, "select all" usually selects current page visible items.
      // If we want to support cross-page selection, we'd need to assume `selectedIds` keeps growing.
      // But clearing logic is "deselect all on current page".
      const newSelectedIds = Array.from(new Set([...selectedIds, ...allIds]));
      onSelectionChange?.(newSelectedIds);
    } else {
      const currentPageIds = data.map((item) => item[idKeyName]);
      const newSelectedIds = selectedIds.filter((id) => !currentPageIds.includes(id));
      onSelectionChange?.(newSelectedIds);
    }
  };

  const handleSelectRow = (id: any) => {
    if (selectedIds.includes(id)) {
      onSelectionChange?.(selectedIds.filter((itemId) => itemId !== id));
    } else {
      onSelectionChange?.([...selectedIds, id]);
    }
  };

  return (
    <>
      {children}

      <div className='relative bg-white dark:bg-gray-800 overflow-x-auto sm:rounded-lg w-full'>
        <table className='w-full mx-0 border my-4 border-gray-300 dark:border-gray-700 table-fixed text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400'>
          <thead className='text-xs text-center capitalize text-gray-700 bg-gray-50 border-b border-t dark:border-gray-900 dark:bg-gray-900 dark:text-gray-500'>
            <tr>
              {showCheckbox && (
                <th className="px-2 py-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th key={column.key as string} className='px-2 py-3 text-left text-sm'>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='bg-white dark:bg-gray-800'>
            {data.map((item, index) => (
              <tr key={index} className='border-b dark:border-gray-700'>
                {showCheckbox && (
                  <td className="px-2 py-3 text-center w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item[idKeyName])}
                      onChange={() => handleSelectRow(item[idKeyName])}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key as string} className='px-2 py-3 text-left text-sm break-words'>
                    {column.viewButton && viewButton && (
                      <p
                        className='text-blue-400 underline cursor-pointer'
                        onClick={() => handleView && handleView(item)}
                      >
                        view
                      </p>
                    )}
                    {column.enableLink && column.path ? (
                      modelForm ? (
                        <p
                          className='text-blue-400 underline cursor-pointer'
                          onClick={() => handleEdit(item[idKeyName])}
                        >
                          {item[column.key]}{" "}
                        </p>
                      ) : item.reports_id === null ? (
                        item[column.key]
                      ) : (
                        <p
                          className='text-blue-400 underline cursor-pointer'
                          onClick={() =>
                            handleNavigation(column.path, item[idKeyName], keyID2 ? item[keyID2] : null)
                          }
                        >
                          {item[column.key]}{" "}
                        </p>
                      )
                    ) : (
                      item[column.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && <p className='p-3 text-sm w-full text-center'>No Record Found</p>}
      </div>
    </>
  );
};

export default CustomTable;
